/** Formats a KPI value so it fits a segment display (digit count + allowed characters). */
import type { SegmentDisplay } from './cluster-model.ts';
import type { SegmentSettings } from './settings.ts';

const CHARSETS = {
  numeric: '0123456789 -',
  alphanumeric: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 -%',
} as const;

export function allowedChars(display: Pick<SegmentDisplay, 'charset'>): string {
  return display.charset === 'numeric' || display.charset === 'alphanumeric' ? CHARSETS[display.charset] : display.charset;
}

/**
 * Returns text of at most `digits` characters (the decimal point does not take a digit when
 * the display has one). Unsupported characters are dropped; values that can't fit become dashes.
 */
export function formatSegment(
  value: number,
  display: Pick<SegmentDisplay, 'digits' | 'charset' | 'decimal_point'>,
  s: Pick<SegmentSettings, 'decimals' | 'compact' | 'suffix'>,
): string {
  const allowed = allowedChars(display);
  const fits = (t: string) => t.replace(display.decimal_point ? '.' : '', '').length <= display.digits;
  const clean = (t: string) =>
    [...t.toUpperCase()].filter((c) => allowed.includes(c) || (c === '.' && display.decimal_point)).join('');

  if (!Number.isFinite(value)) return '-'.repeat(display.digits);

  const suffix = clean(s.suffix);
  const candidates: string[] = [];
  const compactUnits: [number, string][] = [[1e9, 'B'], [1e6, 'M'], [1e3, 'K']];
  for (let d = s.decimals; d >= 0; d--) candidates.push(value.toFixed(d) + suffix);
  if (s.compact) {
    for (const [div, unit] of compactUnits) {
      if (Math.abs(value) >= div && allowed.includes(unit)) {
        for (let d = 2; d >= 0; d--) candidates.push(trimZeros((value / div).toFixed(d)) + unit + suffix);
      }
    }
  }
  for (const c of candidates) {
    // Never drop a decimal point silently: "12.35K" must not turn into "1235K".
    if (c.includes('.') && !display.decimal_point) continue;
    const text = clean(c);
    if (fits(text) && text.length > 0) return text;
  }
  return '-'.repeat(display.digits);
}

/** "12.50" → "12.5", "12.00" → "12", "10" stays "10". */
function trimZeros(t: string): string {
  return t.includes('.') ? t.replace(/0+$/, '').replace(/\.$/, '') : t;
}
